import React, { createRef, RefObject, ReactNode } from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../../../stores/CurrentUserStore'
import { AuditProductAPI, API } from '@getgreenline/homi-shared'
import { AuditStore } from '../EditAuditStore'
import { IProductWithBatchTrackStatus } from '..'
import { Icon } from 'antd'
import { message, Alert, Button, Typography } from 'antd-v4'
import { browserHistory, Link } from 'react-router'
import { Routes } from '../../../../../../constants/Routes'
import { AppView, AppMainView } from '../../../../../../components/AppView'
import Helmet from 'react-helmet'
import AppHeader from '../../../../../../components/AppView/Header'
import { ExportButton } from './ExportButton'
import { BlazeButton } from '../../../../../../components/CustomButtons/BlazeButton'
import { CompleteModal } from './CompleteModal'
import { Main } from './Main'
import { Side } from './Side'
import { parseErrorMsg } from '../../../../../../utilities/helpers'
import { AuditModels, AuditProductModels } from '@getgreenline/audits'
import { sentryWrapper } from '../../../../../../utilities/logging'
import { HelpLinks } from '../../../../../../constants/ExternalLinks'

interface Props {
  audit: AuditModels.CreateInventoryAudit
  auditStore: AuditStore
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface State {
  showCompleteModal: boolean
  isBarcodeListening: boolean
  isCancelling: boolean
  refMainElement: RefObject<HTMLDivElement> | undefined
}

export interface TAuditAlert {
  type: 'success' | 'info' | 'warning' | 'error'
  message: string
  description: ReactNode
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class AuditDetail extends React.Component<Props & InjectedProps, State> {
  company = this.props.currentCompanyStore!.company!

  refAuditTable: RefObject<HTMLDivElement> = createRef()

  constructor(props: Props) {
    super(props)

    this.state = {
      showCompleteModal: false,
      isBarcodeListening: false,
      isCancelling: false,
      refMainElement: undefined,
    }
  }

  createAuditProducts = async (
    products: IProductWithBatchTrackStatus[],
  ): Promise<AuditProductModels.EditAuditProduct[]> => {
    const { audit } = this.props

    const auditProductRecords: AuditProductModels.EditAuditProduct[] = []

    for (const product of products) {
      const duplicateAuditProductExists = !!audit.getAuditProduct(product.productId)

      if (duplicateAuditProductExists) {
        continue
      }

      const addedAuditProduct = await AuditProductAPI.addAuditProduct(this.company.id, {
        inventoryAuditId: audit.id,
        productId: product.productId,
        expectedQuantity: null,
        enteredQuantity: null,
        notes: null,
      })

      const auditProductRecord = audit.addAuditProduct({
        id: addedAuditProduct.id!,
        sku: product.sku,
        barcode: product.barcode,
        productId: product.productId,
        productName: product.productName,
        productUnit: product.productUnit,
        expectedQuantity: addedAuditProduct.expectedQuantity,
        enteredQuantity: addedAuditProduct.enteredQuantity,
        notes: addedAuditProduct.notes,
        isBatchTracked: product.isBatchTracked,
        batches: [],
        cost: 0, // Set cost to 0 as enteredQuantity is always null
        purchasePrice: product.purchasePrice,
      })

      auditProductRecords.push(auditProductRecord)
    }

    return auditProductRecords
  }

  cancelAudit = async () => {
    if (window.confirm('This audit will be cancelled. Are you sure?')) {
      try {
        const auditResponse = await API.cancelInventoryAudit(this.company.id, this.props.audit.id)

        this.props.audit.populate(auditResponse)

        this.props.auditStore.setEditable()
      } catch (error) {
        message.error(parseErrorMsg(error) || 'There was an error cancelling this audit')
      }
    }
  }

  toggleBarcodeListener = async () => {
    await this.setState({
      isBarcodeListening: !this.state.isBarcodeListening,
    })

    this.state.isBarcodeListening
      ? message.info('Please start scanning a product.')
      : message.warn('Stop scanning.')
  }

  focusOnAuditProductRecord = (matchingProductRecord: AuditProductModels.EditAuditProduct) => {
    const refMainElement = this.state.refMainElement

    if (!refMainElement || !refMainElement.current) {
      return
    }

    const refSnapshot = Array.from(refMainElement.current.children).find(
      (element) => element.className === 'audit-table-snapshot-section',
    )
    const refSearch = Array.from(refMainElement.current.children).find(
      (element) => element.id === 'edit-audit-search',
    )
    const refTable = Array.from(refMainElement.current.children).find(
      (element) => element.tagName === 'TABLE',
    )

    if (!refSnapshot || !refTable || !refSearch) {
      return
    }

    const refTBody = Array.from(refTable.children).find((element) => element.tagName === 'TBODY')

    if (!refTBody) {
      return
    }

    const focusAuditProductRecord: Partial<HTMLDivElement> | undefined = Array.from(
      refTBody.children,
    ).find((element) => `id-${matchingProductRecord.productId}` === element.id)

    if (!focusAuditProductRecord) {
      return
    }

    const tBodyOffsetTop = refSnapshot.clientHeight + refSearch.clientHeight

    refMainElement.current.scrollTop = focusAuditProductRecord.offsetTop
      ? focusAuditProductRecord.offsetTop + tBodyOffsetTop
      : 0

    // Highlight the scanned product row
    const duration = 2000

    focusAuditProductRecord.classList!.add('gr-ant-input-focus')
    focusAuditProductRecord.style!.backgroundColor = 'var(--light)'

    setTimeout(() => {
      focusAuditProductRecord.classList?.remove('gr-ant-input-focus')
      focusAuditProductRecord.style!.removeProperty('background-color')
    }, duration)
  }

  get auditAlertContents(): TAuditAlert {
    switch (this.props.audit.status) {
      case 'completed':
        return {
          type: 'success',
          message: 'Audit completed',
          description: 'You can still edit title and notes.',
        }
      case 'cancelled':
        return {
          type: 'error',
          message: 'Audit cancelled',
          description: 'You can still edit title and notes.',
        }
      default:
        return {
          type: 'info',
          message: 'Audit in progress',
          description: (
            <span>
              Currently set to{' '}
              <Link to={Routes.DASHBOARD_SETTINGS_INVENTORY(this.company.id)}>
                {this.company.auditCompleteMode}
              </Link>{' '}
              mode. Learn more about audit modes{' '}
              <Link to={HelpLinks.SETTINGS} target='_blank'>
                here
              </Link>
              .
            </span>
          ),
        }
    }
  }

  render() {
    const { showCompleteModal, isCancelling, isBarcodeListening } = this.state
    const { audit, auditStore } = this.props

    return (
      <AppView column>
        <Helmet>
          <title>Inventory | Audits | Edit</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <AppHeader
          title={audit.title}
          backVisible={true}
          onBackClick={() => browserHistory.push(Routes.DASHBOARD_INVENTORY_AUDIT(this.company.id))}
        >
          <span className='spaced-button-wrapper'>
            <ExportButton audit={audit} />

            {auditStore.isEditable && (
              <>
                <BlazeButton
                  buttonProps={{
                    type: 'danger',
                    icon: 'close',
                    disabled: isCancelling,
                    loading: isCancelling,
                    onClick: this.cancelAudit,
                  }}
                  titleProps={{ title: 'Cancel' }}
                />

                <BlazeButton
                  buttonProps={{
                    type: 'primary',
                    icon: 'check',
                    onClick: () => this.setState({ showCompleteModal: true }),
                  }}
                  titleProps={{ title: 'Complete' }}
                />
              </>
            )}
          </span>

          {showCompleteModal && (
            <CompleteModal
              audit={audit}
              auditStore={auditStore}
              onHide={() => this.setState({ showCompleteModal: false })}
              onSubmit={() => this.setState({ showCompleteModal: false })}
            />
          )}
        </AppHeader>

        <AppMainView className='background-grey'>
          <div className='app-main-view__inner-wrapper'>
            <div className='audit-alert'>
              <Alert
                message={this.auditAlertContents.message}
                description={this.auditAlertContents.description}
                type={this.auditAlertContents.type}
                showIcon
                icon={<Icon type='info-circle' />}
              />

              {audit.status === 'completed' && auditStore.auditProductsWithoutSnapshot.length > 0 && (
                <Alert
                  className='mt-3'
                  message='Expected quantities may be inaccurate for some products'
                  description={
                    <div>
                      <div>
                        <span>This is due to those products having either been </span>
                        <Typography.Text underline>created</Typography.Text>
                        <span>, </span>
                        <Typography.Text underline>activated</Typography.Text>
                        <span>, </span>
                        <Typography.Text underline>undeleted</Typography.Text>
                        <span> or having </span>
                        <Typography.Text underline>inventory tracking turned on</Typography.Text>
                        <span> </span>
                        <span>
                          <b>while</b> the audit was in progress.
                        </span>
                      </div>

                      <div>
                        <span>Please complete a new audit for these products.</span>
                      </div>

                      <Button
                        className='mt-2'
                        onClick={() => {
                          auditStore.setShowAuditProductsWithoutSnapshot(
                            !auditStore.showAuditproductsWithoutSnapshot,
                          )
                        }}
                      >
                        {auditStore.showAuditproductsWithoutSnapshot
                          ? 'View all products'
                          : 'View products'}
                      </Button>
                    </div>
                  }
                  type='warning'
                  showIcon
                />
              )}
            </div>

            <div id='mainV2' ref={this.refAuditTable}>
              <Main
                audit={audit}
                auditStore={auditStore}
                createAuditProducts={this.createAuditProducts}
                isBarcodeListening={isBarcodeListening}
                setRefMainElement={(refMainElement) => this.setState({ refMainElement })}
                focusOnAuditProductRecord={this.focusOnAuditProductRecord}
              />
            </div>

            <div id='sideV2'>
              <Side
                audit={audit}
                auditStore={auditStore}
                createAuditProducts={this.createAuditProducts}
                isBarcodeListening={isBarcodeListening}
                toggleBarcodeListener={this.toggleBarcodeListener}
                focusOnAuditProductRecord={this.focusOnAuditProductRecord}
              />
            </div>
          </div>
        </AppMainView>
      </AppView>
    )
  }
}
